import {
  ShimmeredDetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  IDropdownOption,
  ITextFieldStyles,
  ITooltipHostStyles,
  IconButton,
  Label,
  MessageBar,
  MessageBarType,
  Modal,
  PrimaryButton,
  SelectionMode,
  Stack,
  StackItem,
  Sticky,
  StickyPositionType,
  TextField,
  TooltipHost,
  mergeStyleSets,
} from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { useBoolean } from "@fluentui/react-hooks";
import { useEffect, useMemo, useState } from "react";
import Editor from "react-simple-wysiwyg";

import { ICompany } from "../../../../common/models/ICompany";
import { ICommon } from "../../../../common/models/ICommon";
import CommonService from "../../../../common/api-services/CommonService";
import Pagination from "../../../common/pagination/Pagination";
import { ITac } from "../../../../common/models/ITac";

import styles from "./termsExceptionManagement.module.scss";

const TermsExceptionManagement = () => {
  const service = useMemo(() => new CommonService(), []);
  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] =
    useBoolean(false);
  const [
    isLoadingTacs,
    { setTrue: startLoadingTacs, setFalse: stopLoadingTacs },
  ] = useBoolean(true);
  const [message, setMessage] = useState<string>("");
  const [messageBarType, setMessageBarType] = useState<MessageBarType>();
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [items, setItems] = useState<ITac[]>([]);
  const [tacsFiltered, setTacsFiltred] = useState<ITac[]>([]);
  const [commonItems, setCommonItems] = useState<ICommon[]>([]);
  const [commonTacs, setCommonTacs] = useState<ICommon[]>([]);
  const [edit, isEdit] = useState<boolean>(false);
  const [html, setHtml] = useState("");
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const [tacToDelete, setTacToDelete] = useState<string>("");
  const [validDomainError, setValidDomainError] = useState<string>();
  const [domainConflictError, setDomainConflictError] = useState<string>();

  const [errorMessageFieldRequired, setErrorMessageFieldRequired] =
    useState<string>("");
  const [country, setCountry] = useState<string>("all");
  const [commonTac, setCommonTac] = useState<ICommon>({
    id: crypto.randomUUID(),
    typeId: "TACE",
    company: { companyId: "" },
    tac: { tacId: crypto.randomUUID() },
  });
  let navigate = useNavigate();
  const calloutProps = { gapSpace: 0 };
  const hostStyles: Partial<ITooltipHostStyles> = {
    root: { display: "inline-block" },
  };
  const contentStyles = mergeStyleSets({
    container: {
      display: "flex",
      flexFlow: "column nowrap",
      alignItems: "stretch",
      border: "1px solid",
      minHeight: "400px",
      minWidth: "600px",
      borderRadius: "25px",
      maxHeight: "auto",
    },
    archiveContainer: {
      display: "flex",
      flexFlow: "column nowrap",
      alignItems: "stretch",
      border: "1px solid",
      minHeight: "120px",
      minWidth: "250px",
      borderRadius: "25px",
    },
  });
  const textfieldStyles: Partial<ITextFieldStyles> = {
    root: {
      width: 350,
      paddingRight: 50,
      paddingBottom: 10,
    },
  };
  const columns: IColumn[] = [
    {
      key: "column3.5",
      name: "Recipient",
      fieldName: "clientName",
      minWidth: 100,
      maxWidth: 150,
      isRowHeader: true,
    },
    {
      key: "column3",
      name: "Domain part",
      fieldName: "domainPart",
      minWidth: 100,
      maxWidth: 150,
      isRowHeader: true,
    },
    {
      key: "column1",
      name: "Country",
      fieldName: "companyId",
      minWidth: 100,
      maxWidth: 200,
      isRowHeader: true,
      onRender: (item) => {
        const company = commonItems?.find(
          (ci) => ci.company?.companyId === item.companyId,
        );
        return (
          <>
            <Stack horizontalAlign="start">{company?.company?.country}</Stack>
          </>
        );
      },
    },
    {
      key: "column2",
      name: "Entity name",
      fieldName: "companyId",
      minWidth: 100,
      maxWidth: 200,
      isRowHeader: true,
      onRender: (item) => {
        const company = commonItems?.find(
          (ci) => ci.company?.companyId === item.companyId,
        );
        return (
          <>
            <Stack horizontalAlign="start">
              {company?.company?.companyName}
            </Stack>
          </>
        );
      },
    },

    {
      key: "column8",
      name: "",
      fieldName: "",
      minWidth: 100,
      maxWidth: 100,
      isRowHeader: true,
      data: "string",
      isPadded: false,
      onRender: (item: any) => {
        return (
          <Stack horizontal horizontalAlign="end">
            <TooltipHost
              content="Edit exeception"
              calloutProps={calloutProps}
              styles={hostStyles}
            >
              <IconButton
                iconProps={{ iconName: "Edit" }}
                className={styles.actionIconButton}
                onClick={() => {
                  onClickEditIcon(item);
                }}
              />
            </TooltipHost>
            <TooltipHost
              content="Delete exception"
              calloutProps={calloutProps}
              styles={hostStyles}
            >
              <IconButton
                iconProps={{ iconName: "Delete" }}
                className={styles.actionIconButton}
                onClick={() => {
                  const tacCommonItem = commonTacs?.find(
                    (ci) => ci.tac?.tacId === item.tacId,
                  );
                  if (tacCommonItem?.id) setTacToDelete(tacCommonItem.id);
                }}
              />
            </TooltipHost>
          </Stack>
        );
      },
    },
  ];

  function onChange(e: { target: { value: string } }) {
    setHtml(e.target.value);
    let obj = commonTac.tac;
    if (obj) {
      obj = { ...obj, tacContent: e.target.value };
      setCommonTac({ ...commonTac, tac: obj });
    }
  }

  const onSaveTacException = () => {
    let objTac = commonTac.tac;
    if (objTac) {
      objTac.isCurrent = true;
      objTac.tacValidFrom = new Date();
      // Store the domain part case-insensitively (lower-cased) so it can never
      // create a "@gmail.com" vs "@Gmail.com" duplicate.
      objTac.domainPart = objTac.domainPart?.trim().toLowerCase();
    }
    let resCommonTac = { ...commonTac, tac: objTac };
    if (
      resCommonTac.tac?.companyCode &&
      resCommonTac.tac?.companyId &&
      resCommonTac.tac?.domainPart &&
      resCommonTac.tac?.clientName &&
      !validDomainError &&
      !domainConflictError
    ) {
      setErrorMessageFieldRequired("");
      service.Create([resCommonTac] as ICommon[]).then((result: any) => {
        if (result?.error) {
          setMessageBarType(MessageBarType.error);
          setMessage(`Error: ${result?.error}`);
        } else {
          setMessageBarType(MessageBarType.success);
          setMessage(`Exception added successfully`);
          getTacExceptions(commonItems);
        }
        resetModal();
      });
    } else {
      setErrorMessageFieldRequired("Field is required");
    }
  };
  const onEditTac = () => {
    if (
      commonTac.tac?.companyCode &&
      commonTac.tac?.companyId &&
      commonTac.tac?.domainPart &&
      commonTac.tac?.clientName &&
      !validDomainError &&
      !domainConflictError
    ) {
      setErrorMessageFieldRequired("");
      const normalizedTac = {
        ...commonTac,
        tac: {
          ...commonTac.tac,
          domainPart: commonTac.tac?.domainPart?.trim().toLowerCase(),
        },
      };
      service.Update(normalizedTac).then((result: any) => {
        if (result?.error) {
          setMessageBarType(MessageBarType.error);
          setMessage(`Error: ${result?.error}`);
        } else {
          getTacExceptions(commonItems);
          setMessageBarType(MessageBarType.success);
          setMessage(`Exception updated successfully`);
        }
        resetModal();
      });
    } else {
      setErrorMessageFieldRequired("Field is required");
    }
  };

  const onDeleteTac = (id: string) => {
    service.Delete(id).then((result: any) => {
      if (result?.error) {
        setMessageBarType(MessageBarType.error);
        setMessage(`Error: ${result?.error}`);
      } else {
        setMessageBarType(MessageBarType.success);
        setMessage(`Exception deleted successfully`);
        getTacExceptions(commonItems);
        setTacToDelete("");
      }
    });
  };
  const changeCurrentPage = (newPageNumber: number) => {
    setCurrentPage(newPageNumber);
  };
  const getTacExceptions = (comps: ICommon[]) => {
    startLoadingTacs();
    service
      .GetTacExceptions()
      .then((data: any[]) => {
        if (data) {
        data = data.sort((a, b) => {
          const aCompany = comps?.find(
            (ci) => ci.company?.companyId === a.tac.companyId,
          );
          const bCompany = comps?.find(
            (ci) => ci.company?.companyId === b.tac.companyId,
          );
          // Primary sort: domain part (case-insensitive, so "@gmail.com" and
          // "@Gmail.com" group together). Secondary sort: country.
          const domainCompare = (a.tac.domainPart || "").localeCompare(
            b.tac.domainPart || "",
            undefined,
            { sensitivity: "base" },
          );
          if (domainCompare !== 0) return domainCompare;
          return (aCompany?.company?.country || "").localeCompare(
            bCompany?.company?.country || "",
          );
        });
        setCommonTacs(data);
        let tacExceptions: ITac[] = [];
        data.forEach((e) => {
          tacExceptions.push(e.tac);
        });
        if (tacExceptions) {
          setItems(tacExceptions);
        }
        }
      })
      .finally(() => {
        stopLoadingTacs();
      });
  };
  const getCompanies = async () => {
    let data = await service.GetAllCompanies();
    data = data.sort(
      (a, b) =>
        a.company?.country?.localeCompare(b.company?.country || "") || 0,
    );

    if (data) {
      setCommonItems(data);
    }

    return data;
  };
  const resetModal = () => {
    hideModal();
    setCommonTac({
      id: crypto.randomUUID(),
      typeId: "TACE",
      company: { companyId: "" },
      tac: { tacId: crypto.randomUUID() },
    });
    setCountry("all");
    setHtml("");
    setValidDomainError("");
    setErrorMessageFieldRequired("");
    setDomainConflictError("");
    isEdit(false);
  };

  const onClickEditIcon = (item: ITac) => {
    if (item.tacId) {
      const commonTacItem = commonTacs.find((x) => x.tac?.tacId === item.tacId);
      if (commonTacItem) {
        isEdit(true);
        showModal();
        setCommonTac(commonTacItem!);
        setHtml(item.tacContent || "");
        const tacComp = commonItems?.find(
          (ci) => ci.company?.companyId === commonTacItem.tac?.companyId,
        );

        setCountry(tacComp?.company?.country || "all");
      }
    }
  };

  const onCompanySelected = (item: ICompany) => {
    if (item.tacId) {
      service.GetTacbyTacId(item.tacId).then((data: any) => {
        if (data?.error) {
          setMessageBarType(MessageBarType.error);
          setMessage(`Error: ${data?.error}`);
        } else {
          setCountry(item.country || "all");
          setCommonTac({
            ...commonTac,
            tac: {
              ...commonTac.tac,
              companyId: item.companyId,
              companyCode: item.companyCode,
              tacContent:
                data?.tac.tacContent || commonTac.tac?.tacContent || "",
            },
          });
          if (
            items.find(
              (ti) =>
                ti.domainPart?.trim().toLowerCase() ===
                  commonTac.tac?.domainPart?.trim().toLowerCase() &&
                ti.companyId === item.companyId,
            )
          ) {
            setDomainConflictError(
              "This domain already has an exception for the selected entity",
            );
          } else {
            setDomainConflictError("");
          }
          if (data?.tac.tacContent) {
            setHtml(data?.tac.tacContent);
          }
        }
      });
    }
  };

  const filterTacs = () => {
    const countryFilteredTacs =
      countryFilter === "all"
        ? items
        : items.filter(
            (tac) =>
              !!commonItems.find(
                (ci) =>
                  ci.company?.companyId === tac.companyId &&
                  ci.company?.country === countryFilter,
              ),
          );
    if (countryFilteredTacs.length < pageSize) {
      setTotalPages(1);
    } else {
      setTotalPages(Math.ceil(countryFilteredTacs.length / pageSize));
    }
    setTacsFiltred(
      countryFilteredTacs.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    );
  };

  useEffect(() => {
    getCompanies().then((companies) => {
      getTacExceptions(companies);
      const localCurrentUser = localStorage.getItem("currentUser");
      if (typeof localCurrentUser === "string") {
        const user = JSON.parse(localCurrentUser);
        if (
          user.isLegal == false ||
          user == undefined ||
          user.isLegal == undefined
        ) {
          navigate("/notfound");
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    filterTacs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items), currentPage, countryFilter]);

  const countriesOptions: IDropdownOption[] = [
    {
      key: "all",
      text: "All",
    },
  ];

  commonItems?.forEach((ci) => {
    if (
      ci.company &&
      ci.company.companyId &&
      !countriesOptions.find((co) => co.key === ci.company?.country)
    ) {
      const hasOnlyDeletedCompanies = commonItems
        .filter((c) => c.company?.country === ci.company?.country && c.company)
        .every((c) => c.company?.isDeleted);

      countriesOptions.push({
        key: ci.company?.country || "",
        text: ci.company?.country || "",
        disabled: hasOnlyDeletedCompanies,
        hidden: hasOnlyDeletedCompanies,
      });
    }
  });

  return (
    <>
      <Stack>
        <Stack horizontal horizontalAlign="space-between">
          <Stack horizontal horizontalAlign="start">
            <Label className={styles.PageHeader}>
              Terms exceptions management
            </Label>
          </Stack>
        </Stack>

        <Stack>
          <hr className={styles.horizontalLine}></hr>
        </Stack>
        {message != "" && (
          <Stack>
            <MessageBar
              messageBarType={messageBarType}
              isMultiline={true}
              dismissButtonAriaLabel="Close"
              onDismiss={() => {
                setMessage("");
                setMessageBarType(undefined);
              }}
            >
              {message}
            </MessageBar>
          </Stack>
        )}
        <Stack horizontal horizontalAlign="space-between">
          <Dropdown
            style={{ width: 200 }}
            placeholder="Select a country"
            label="Country"
            selectedKey={countryFilter}
            options={countriesOptions}
            onChange={(_, val) => {
              if (typeof val?.key === "string") {
                setCountryFilter(val.key);
              }
            }}
          />

          <TooltipHost
            content="Add Terms exception"
            // This id is used on the tooltip itself, not the host
            // (so an element with this id only exists when the tooltip is shown)
            // id={tooltipArchiveId}
            calloutProps={calloutProps}
            styles={hostStyles}
          >
            <IconButton
              iconProps={{ iconName: "Add" }}
              className={styles.btnAddItem}
              onClick={showModal}
            ></IconButton>
          </TooltipHost>
        </Stack>
        <Stack>
          <StackItem>
            <ShimmeredDetailsList
              items={tacsFiltered}
              enableShimmer={isLoadingTacs}
              compact={false}
              columns={columns}
              selectionMode={SelectionMode.none}
              setKey="none"
              layoutMode={DetailsListLayoutMode.fixedColumns}
              isHeaderVisible={true}
            />
          </StackItem>
          <Sticky stickyPosition={StickyPositionType.Footer}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={changeCurrentPage}
            />
          </Sticky>
        </Stack>
      </Stack>
      <Modal
        isOpen={!!tacToDelete}
        isBlocking={false}
        containerClassName={contentStyles.archiveContainer}
        onDismiss={() => {
          setTacToDelete("");
        }}
      >
        {tacToDelete && (
          <Stack style={{ padding: 20 }}>
            <Label className={styles.contentHeader}>
              You are about to delete this exception. Please confirm to proceed
            </Label>
            <Stack gap={16} horizontal horizontalAlign="center">
              <PrimaryButton
                text="Confirm"
                className={styles.buttonSave}
                onClick={() => onDeleteTac(tacToDelete)}
              />
              <PrimaryButton
                text="Cancel"
                className={styles.buttonCancel}
                onClick={() => {
                  setTacToDelete("");
                }}
              />
            </Stack>
          </Stack>
        )}
      </Modal>
      <Modal
        isOpen={isModalOpen}
        onDismiss={resetModal}
        isBlocking={false}
        containerClassName={contentStyles.container}
      >
        <Stack style={{ padding: 20 }}>
          <Label className={styles.contentHeader}>
            Terms exceptions management
          </Label>
          <Stack>
            <hr className={styles.horizontalLine}></hr>
          </Stack>
          <Stack horizontal horizontalAlign="start">
            <TextField
              label="Recipient"
              placeholder="Recipient company name"
              required
              value={commonTac.tac?.clientName || ""}
              errorMessage={
                commonTac.tac?.clientName == "" ||
                commonTac.tac?.clientName == undefined
                  ? errorMessageFieldRequired
                  : undefined
              }
              onChange={(ev, val) => {
                setCommonTac({
                  ...commonTac,
                  tac: { ...commonTac.tac, clientName: val },
                });
              }}
              styles={textfieldStyles}
            />
            <TextField
              label="Domain part"
              required
              placeholder="@client.com"
              value={commonTac.tac?.domainPart || ""}
              errorMessage={
                (commonTac.tac?.domainPart == "" ||
                commonTac.tac?.domainPart == undefined
                  ? errorMessageFieldRequired
                  : undefined) ||
                validDomainError ||
                domainConflictError
              }
              onChange={(ev, val) => {
                setCommonTac({
                  ...commonTac,
                  tac: { ...commonTac.tac, domainPart: val },
                });
                const domainPattern = /^@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/;
                if (!domainPattern.test(val || "")) {
                  setValidDomainError("Invalid domain");
                } else {
                  setValidDomainError("");
                }
                if (
                  items.find(
                    (ti) =>
                      ti.domainPart?.trim().toLowerCase() ===
                        val?.trim().toLowerCase() &&
                      ti.companyId === commonTac.tac?.companyId,
                  )
                ) {
                  setDomainConflictError(
                    "This domain already has an exception for the selected entity",
                  );
                } else {
                  setDomainConflictError("");
                }
              }}
              styles={textfieldStyles}
            />
            <Dropdown
              options={countriesOptions.filter((c) => c.key !== "all")}
              label="Country"
              defaultSelectedKey={country}
              selectedKey={country}
              styles={{
                root: {
                  width: 300,
                  paddingRight: 50,
                  paddingBottom: 10,
                },
              }}
              onChange={(ev, val) => {
                if (typeof val?.key === "string") {
                  const newCompany = commonItems?.find(
                    (ci) =>
                      ci.company?.country === val.key && !ci.company?.isDeleted,
                  );
                  if (newCompany?.company) {
                    onCompanySelected(newCompany.company);
                    setCountry(val?.key);
                    setValidDomainError("");
                  }
                }
              }}
            />
            <Dropdown
              label="Entity name"
              selectedKey={commonTac.tac?.companyId || ""}
              required
              options={
                commonItems
                  ?.filter(
                    (c) =>
                      c.company?.companyId &&
                      (country === "all" || c.company.country === country),
                  )
                  .map((c) => ({
                    key: c.company?.companyId || "",
                    disabled: c.company?.isDeleted,
                    hidden: c.company?.isDeleted,
                    text:
                      c.company?.companyName && c.company?.companyCode
                        ? c.company?.companyName +
                          " (" +
                          c.company?.companyCode +
                          ")"
                        : "",
                  })) || []
              }
              errorMessage={
                commonTac.tac?.companyId == "" ||
                commonTac.tac?.companyId == undefined
                  ? errorMessageFieldRequired
                  : undefined
              }
              styles={{
                root: {
                  width: 300,
                  paddingRight: 50,
                  paddingBottom: 10,
                },
              }}
              onChange={(ev, val) => {
                if (typeof val?.key === "string") {
                  const newCompany = commonItems?.find(
                    (ci) => ci.company?.companyId === val.key,
                  );
                  if (newCompany?.company) {
                    onCompanySelected(newCompany.company);
                  }
                }
              }}
            />
          </Stack>
          <Stack>
            <hr className={styles.horizontalLine}></hr>
          </Stack>
          <Label className={styles.contentHeader}>Terms</Label>
          <Stack>
            <Editor
              disabled={!commonTac.tac?.companyId}
              value={html}
              onChange={onChange}
              style={{ maxHeight: 300, overflow: "auto" }}
            />
          </Stack>
          <Stack horizontal horizontalAlign="end" style={{ paddingTop: 20 }}>
            <PrimaryButton
              text={!edit ? "Add" : "Save"}
              className={styles.buttonSave}
              disabled={false}
              onClick={!edit ? onSaveTacException : onEditTac}
            />
            <PrimaryButton
              text="Cancel"
              className={styles.buttonCancel}
              onClick={resetModal}
            />
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};
export default TermsExceptionManagement;
